import { FunctionComponent, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { faDownload } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { useSnackBar } from "@/utils/useSnackbar";

import { useLazyDownloadAnalyticsExportQuery } from "../../../redux/analytics/analytics.api";
import { saveFileFromBlob } from "../../Contacts/utils/downloadFile";
import { AnalyticsRequest } from "../schema/analytics";

interface AnalyticsExportButtonProps {
	payload: AnalyticsRequest;
	disabled?: boolean;
}

/**
 * Downloads the analytics XLSX export (per-campaign + per-list sheets) for
 * the current page selection (lists, campaigns, date range, filters).
 */
export const AnalyticsExportButton: FunctionComponent<AnalyticsExportButtonProps> = ({
	payload,
	disabled,
}) => {
	const { t } = useTranslation();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();
	const [downloadAnalyticsExport, { isFetching }] = useLazyDownloadAnalyticsExportQuery();

	const handleExport = useCallback(async () => {
		try {
			const fileName = `analytics-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
			const downloadPromise = downloadAnalyticsExport(payload).unwrap();

			await saveFileFromBlob(downloadPromise, fileName, {
				description: "Excel File",
				mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
				extension: "xlsx",
			});
		} catch (error) {
			console.error(error);
			handleOpenSnackbar(t("basics.error"));
		}
	}, [downloadAnalyticsExport, payload, handleOpenSnackbar, t]);

	return (
		<>
			<Button
				color={ButtonColor.ACTION_SECONDARY}
				disabled={disabled}
				image={<Icon className="mr-1.5 w-3 h-3" icon={faDownload} />}
				isLoading={isFetching}
				size={ButtonSize.S}
				title={t("analytics.export")}
				type="button"
				onClick={handleExport}
			/>

			{renderMessageSnackbar()}
		</>
	);
};
